'use client'
import React from 'react'
import axios from 'axios'
import Wallet from '@/utils/wallet'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import useWallet from '@/utils/hooks/useWallet'
import useTokenOutpoints from '../../hooks/useTokenOutpoints'
import { API_URL, EXPLORER_URL, NETWORK, NETWORK_CONFIG } from '@/utils/constants'
import {
	planTapListings,
	recommendedTapOutputCount,
	PreparedTapListings
} from '@/utils/tap-listing-plan'
import { Button } from '@/components/ui/button'
import TapListingFields from './tap-listing-fields'
import useFeeEstimates from '@/utils/hooks/useFeeEstimates'
import Invoice from '../../components/invoice'
import Tabs from './tabs'
import styles from './tap-prepare.module.css'
import formatNumber from '@/utils/format-number'
import { formatSatoshis } from '@/utils/hooks/useSatoshiPrice'

type Pending = { txid: string; price: string; count: number; uncertain?: boolean }
export default function TapPrepare({
	args,
	token,
	tab,
	onChangeTab,
	askingPriceSats,
	onPrepared
}: {
	args: { slug: string }
	token: any
	tab: string
	onChangeTab: (tab: string) => void
	askingPriceSats?: number
	onPrepared: (value: PreparedTapListings) => void
}) {
	const ticker = args.slug.slice(4)
	const address = Wallet.getTaprootAddress()
	const storageKey = `tap-preparation:v1:${address}:${args.slug}`
	const { exchangeRate } = useExchangeRate()
	const { feeRate: selectedFeeRate } = useFeeEstimates()
	const { mutate } = useWallet()
	const { data: transfers } = useTokenOutpoints(token)
	const available = String(token?.available_balance || '0')
	// Follow a late-arriving balance until the user moves the amount slider.
	const [selectedAmount, setAmount] = React.useState<string | null>(null)
	const amount = selectedAmount ?? available
	const floorPrice =
		askingPriceSats && Number.isFinite(askingPriceSats) && askingPriceSats > 0
			? askingPriceSats.toFixed(8).replace(/\.?0+$/, '')
			: ''
	const effectivePrice = floorPrice
	const [selectedOutputs, setOutputs] = React.useState<number | null>(null)
	const [pending, setPending] = React.useState<Pending | null>(() => {
		try {
			const value = JSON.parse(sessionStorage.getItem(storageKey) || 'null')
			return value &&
				/^[a-f0-9]{64}$/.test(value.txid) &&
				Number.isInteger(value.count) &&
				value.count >= 1 &&
				value.count <= 24 &&
				typeof value.price === 'string'
				? value
				: null
		} catch {
			return null
		}
	})
	const [confirmedIds, setConfirmedIds] = React.useState<string[]>([])
	const [pollError, setPollError] = React.useState(false)
	const [walletError, setWalletError] = React.useState(false)
	const [busy, setBusy] = React.useState(false)
	const [estimate, setEstimate] = React.useState<{
		key: string
		network_fee: number
		base_fee: number
		size_fee: number
	} | null>(null)
	const estimateSequence = React.useRef(0)
	const quoted = React.useRef<{ key: string; files: File[]; feeRate: number } | null>(null)
	const decimals = ticker.toLowerCase().startsWith('dmt-')
		? 0
		: Number(token?.decimals ?? token?.dec ?? 18)
	let plan: ReturnType<typeof planTapListings> | undefined
	let error = ''
	let recommended = 1
	let outputs = selectedOutputs ?? recommended
	try {
		const total = planTapListings(
			amount,
			available,
			decimals,
			effectivePrice,
			'single',
			exchangeRate
		)
		recommended = recommendedTapOutputCount(total.gross, exchangeRate)
		outputs = selectedOutputs ?? recommended
		plan = planTapListings(amount, available, decimals, effectivePrice, outputs, exchangeRate)
	} catch (e) {
		error = (e as Error).message
	}
	const key = JSON.stringify([
		amount,
		effectivePrice,
		outputs,
		selectedFeeRate,
		exchangeRate,
		address,
		token?.available_balance
	])
	const currentKey = React.useRef(key)
	React.useEffect(() => {
		currentKey.current = key
	}, [key])
	const postage = Wallet.provider?.capabilities?.includes('payments-wallet')
		? 1000
		: (NETWORK_CONFIG as any).postage
	const usd = (sats: number) =>
		new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
			(sats / 1e8) * exchangeRate
		)

	const formatFee = (sats: number) => `${formatSatoshis(sats)} ${NETWORK} / ${usd(sats)}`
	const hasAvailable = Number(available) > 0
	const hasTransferable = Number(token?.transferable_balance) > 0

	const pendingTxid = pending?.txid
	const pendingCount = pending?.count
	React.useEffect(() => {
		// The signed txid is saved before broadcast. Do not query it until the
		// submission finishes: an explorer 404 at that point is expected.
		if (!pendingTxid || !pendingCount || busy) return
		let cancelled = false
		let timer: ReturnType<typeof setTimeout>
		async function poll() {
			// Explorer availability and wallet indexing are independent. A wallet
			// refresh failure must never turn a successful broadcast into a warning.
			void Promise.resolve()
				.then(() => mutate())
				.then(
					() => {
						if (!cancelled) setWalletError(false)
					},
					() => {
						if (!cancelled) setWalletError(true)
					}
				)
			try {
				const { data } = await axios.get(`${EXPLORER_URL}/api/tx/${pendingTxid}/outspends`, {
					timeout: 15000
				})
				if (cancelled) return
				if (!Array.isArray(data)) throw new Error('Invalid confirmation response')
				const outputs = data.slice(0, pendingCount)
				setConfirmedIds(
					outputs
						.filter((v: any) => v.spent && v.status?.confirmed && /^[a-f0-9]{64}$/.test(v.txid))
						.map((v: any) => `${v.txid}i0`)
				)
				setPollError(false)
				// Finding the transaction proves submission, even if /inscribe/pay's
				// response was lost. Persist recovery so reopening stays accurate.
				setPending((current) => {
					if (!current?.uncertain || current.txid !== pendingTxid) return current
					const recovered = { ...current, uncertain: false }
					try {
						sessionStorage.setItem(storageKey, JSON.stringify(recovered))
					} catch {
						/* In-memory tracking still works. */
					}
					return recovered
				})
			} catch {
				if (!cancelled) setPollError(true)
			}
			if (!cancelled) timer = setTimeout(poll, 15000)
		}
		void poll()
		return () => {
			cancelled = true
			clearTimeout(timer)
		}
	}, [pendingTxid, pendingCount, busy, mutate, storageKey])
	const readyIds = confirmedIds.filter((id) =>
		transfers.some((t) => t.inscription_id === id && !t.escrow)
	)

	async function build(deps: any[], feeRate: number) {
		const [requestKey, amounts] = deps as [string, string[]]
		const sequence = ++estimateSequence.current
		const files = amounts.map(
			(amt, i) =>
				new File(
					[JSON.stringify({ p: 'tap', op: 'token-transfer', tick: ticker, amt })],
					`tap-${i}.txt`,
					{ type: 'text/plain' }
				)
		)
		const { data } = await axios.post(`${API_URL}/inscribe/estimate`, {
			destination: address,
			fee_rate: feeRate,
			postage,
			file_sizes: files.map((f) => f.size),
			content_type: 'text/plain'
		})
		if (currentKey.current === requestKey && sequence === estimateSequence.current) {
			setEstimate({ ...data, key: requestKey })
			quoted.current = { key: requestKey, files, feeRate }
		}
		return { psbt: requestKey, fee: BigInt(data.network_fee) }
	}
	async function submit(quotedKey: string) {
		const quote = quoted.current
		if (!plan || !quote || quote.key !== key || quotedKey !== key)
			throw new Error('Wait for the updated preparation estimate.')
		setBusy(true)
		try {
			const psbt = await Wallet.getInscribe(
				quote.files.map((file) => ({ file })),
				quote.feeRate,
				postage
			)
			const { rawtx, success } = await Wallet.signInscribe(psbt)
			if (!success || !rawtx) throw new Error('Preparation was not signed.')
			if (Wallet.getTaprootAddress() !== address)
				throw new Error('Wallet changed. Reopen preparation with the original wallet.')
			// Persist the signed transaction ID before submission. A dropped response
			// must not invite a second inscription purchase for the same inventory.
			const { Transaction } = await import('bitcoinjs-lib')
			const next: Pending = {
				txid: Transaction.fromHex(rawtx).getId(),
				price: effectivePrice,
				count: plan.amounts.length
			}
			const remember = (value: Pending) => {
				setPending(value)
				try {
					sessionStorage.setItem(storageKey, JSON.stringify(value))
				} catch {
					/* In-memory tracking still works. */
				}
			}
			remember({ ...next, uncertain: true })
			try {
				const { data } = await axios.post(`${API_URL}/inscribe/pay`, { rawtx }, { timeout: 30000 })
				if (!/^[a-f0-9]{64}$/.test(data.txid)) throw new Error('Missing transaction ID')
				remember({ ...next, txid: data.txid })
			} catch {
				remember({ ...next, uncertain: true })
			}
		} finally {
			setBusy(false)
		}
	}
	return (
		<div className={styles.root}>
			<div className="flex px-2 h-7 w-full justify-between items-center">
				<p className="text-muted-foreground text-xs font-medium">My Wallet</p>
			</div>
			<div className="flex px-2 py-1.5 gap-4 items-center">
				<Tabs value={tab} onValueChange={onChangeTab} slug={args.slug} />
			</div>
			{pending ? (
				<div className="p-4 space-y-4" aria-live="polite">
					<h2 className="text-base font-semibold">
						{readyIds.length === pending.count
							? 'Your listings are ready to review'
							: 'Preparing your listings'}
					</h2>
					<p className="text-sm text-muted-foreground">
						{readyIds.length} of {pending.count} transfers confirmed and available. Your asking
						price is saved. You will review and sign the listings next.
					</p>
					{readyIds.length !== pending.count && (
						<div className="space-y-2 text-xs">
							<p
								className={pending.uncertain && !busy ? 'text-amber-500' : 'text-muted-foreground'}
							>
								{busy
									? 'Submitting your preparation transaction…'
									: pending.uncertain
									? 'Submission response was interrupted. We’re checking your transaction automatically. Do not prepare this batch again.'
									: confirmedIds.length === pending.count
									? 'Preparation confirmed. Waiting for your wallet to update.'
									: 'Transaction submitted. Waiting for Bitcoin confirmation.'}
							</p>
							{!busy && !pending.uncertain && (pollError || walletError) && (
								<p className="text-muted-foreground">
									{pollError
										? 'Confirmation updates are temporarily unavailable. We’ll retry automatically.'
										: 'Wallet updates are temporarily unavailable. We’ll retry automatically.'}
								</p>
							)}
						</div>
					)}
					<a
						className="text-sm underline block"
						href={`${EXPLORER_URL}/tx/${pending.txid}`}
						target="_blank"
						rel="noreferrer"
					>
						View preparation transaction
					</a>
					<Button
						className="w-full"
						disabled={readyIds.length !== pending.count}
						onClick={() => {
							onPrepared({ ids: readyIds, price: pending.price })
							try {
								sessionStorage.removeItem(storageKey)
							} catch {
								/* Storage may be disabled. */
							}
						}}
					>
						Review {pending.count} {pending.count === 1 ? 'listing' : 'listings'}
					</Button>
				</div>
			) : !hasAvailable ? (
				<div className="flex flex-col items-center gap-3 px-2 py-8 text-xs text-center">
					<p>
						{hasTransferable
							? "You've already split this token"
							: "You don't have any available balance to split."}
					</p>
					{hasTransferable && (
						<button className="min-h-11 px-3 text-violet-500" onClick={() => onChangeTab('list')}>
							List now
						</button>
					)}
				</div>
			) : (
				<>
					<TapListingFields
						busy={busy}
						amount={amount}
						setAmount={setAmount}
						available={available}
						decimals={decimals}
						outputs={outputs}
						setOutputs={setOutputs}
						recommended={recommended}
						plan={plan}
						error={error}
						usd={usd}
					/>

					{plan && (
						<Invoice
							open
							onBuild={build}
							buildDeps={[key, plan.amounts]}
							onSubmit={submit}
							disabled={busy || estimate?.key !== key}
							action={`Split ${formatNumber(amount)} TAP`}
							items={[
								{
									title: 'Postage',
									valueFunc: ({ building }) =>
										building || estimate?.key !== key
											? '—'
											: formatFee(postage * plan!.amounts.length)
								},
								{
									title: 'Service Fee',
									valueFunc: ({ building }) =>
										building || estimate?.key !== key
											? '—'
											: formatFee(estimate.base_fee + estimate.size_fee)
								},
								{ title: 'Network Fee' },
								{
									title: 'Total Fee',
									emphasize: true,
									valueFunc: ({ building }) =>
										building || estimate?.key !== key
											? '—'
											: formatFee(
													estimate.network_fee +
														estimate.base_fee +
														estimate.size_fee +
														postage * plan!.amounts.length
											  )
								}
							]}
						/>
					)}
				</>
			)}
		</div>
	)
}
